import {
  authHeaders,
  errorText,
  isApiSuccess,
  pick,
  readJson,
} from "../Offices/officeHelpers";
import { API_ENDPOINT } from "../../services/api";
import Labels from "../../services/apiLabels";
import {
  formatAppointmentStatusLabel,
  getAppointmentStatusTone,
  resolveAppointmentStatus,
} from "../../../utils/appointmentBooking";

export function evaluationOf(item) {
  const ev = item?.evaluation;
  return ev && typeof ev === "object" && !Array.isArray(ev) ? ev : {};
}

export function nestedAppointment(item) {
  const ev = evaluationOf(item);
  const list = Array.isArray(ev.appointment) ? ev.appointment : [];
  const match = list.find((row) => String(row?.id) === String(item?.id));
  if (match) return match;
  if (list[0]) return list[0];
  if (item?.date_selected || item?.time_selected) return item;
  return {};
}

export function appointmentId(item) {
  const id = pick(item, ["id", "pk", "appointment_id"]);
  if (id != null && id !== "") return id;
  return pick(nestedAppointment(item), ["id", "pk", "appointment_id"], null);
}

export function clientName(item) {
  const full = `${item?.first_name || ""} ${item?.last_name || ""}`.trim();
  return full || item?.username || evaluationOf(item).Website_user || "—";
}

export function vehicleTitle(item) {
  const ev = evaluationOf(item);
  const title = [ev.brand_name, ev.model_name].filter(Boolean).join(" ");
  return title || ev.variant_name || "Vehicle";
}

/** Brand logo from evaluation.brand_image (office_appointments_listing). */
export function vehicleBrandImage(item) {
  const ev = evaluationOf(item);
  const candidates = [
    ev.brand_image,
    ev.Brand_image,
    ev.brands_image,
    item?.brand_image,
    item?.brands_image,
  ];
  for (const value of candidates) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return "";
}

export function vehicleReg(item) {
  return evaluationOf(item).reg_number || "—";
}

export function trackingNumber(item) {
  return evaluationOf(item).tracking_number || "—";
}

export function evaluationId(item) {
  const ev = evaluationOf(item);
  const id = ev.id ?? ev.pk;
  if (id != null && id !== "") return id;
  const raw = item?.evaluation;
  if (raw != null && typeof raw !== "object") return raw;
  return "—";
}

export function vehicleAddressText(item) {
  const addr = (evaluationOf(item).vehicle_address || [])[0];
  if (!addr || typeof addr !== "object") return "—";
  return (
    [addr.line1, addr.line2, addr.city, addr.post_code, addr.country]
      .map((part) => String(part || "").trim())
      .filter(Boolean)
      .join(", ") ||
    addr.address ||
    "—"
  );
}

export function vehicleFeatures(item) {
  const details = evaluationOf(item).vehcile_details;
  if (!Array.isArray(details)) return [];
  return details
    .map((row) => {
      const answer = (Array.isArray(row?.feature) ? row.feature : [])
        .map((feat) => feat?.choice)
        .filter(Boolean)
        .join(", ");
      return {
        question: row?.question_ || "Detail",
        answer: answer || "—",
      };
    })
    .filter((row) => row.question);
}

export function imageGroups(item) {
  const ev = evaluationOf(item);
  return [
    { label: "Exterior", items: ev.exterior_images_ },
    { label: "Interior", items: ev.interior_images_ },
    { label: "Wheels", items: ev.wheels_images_ },
    { label: "Treads", items: ev.treads_images_ },
    { label: "Damage", items: ev.damage_images_ },
  ]
    .map((group) => ({
      ...group,
      items: Array.isArray(group.items)
        ? group.items.filter((img) => img?.image)
        : [],
    }))
    .filter((group) => group.items.length);
}

export function nextAppointment(office) {
  const next = office?.next_appointment;
  if (!next || typeof next !== "object") return null;
  return next;
}

export function formatNextAppointment(office) {
  const next = nextAppointment(office);
  if (!next) return "—";
  const date = appointmentDate(next);
  const time = formatAppointmentTime(appointmentTime(next));
  const parts = [date !== "—" ? date : "", time !== "—" ? time : ""].filter(
    Boolean
  );
  return parts.join(" · ") || "—";
}

export function formatIsoDate(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function defaultAppointmentDateRange() {
  const start = new Date();
  start.setDate(start.getDate() - 30);
  const end = new Date();
  end.setDate(end.getDate() + 30);
  return {
    starting_date: formatIsoDate(start),
    ending_date: formatIsoDate(end),
  };
}

export function parseAppointmentsPage(payload) {
  const root = payload?.data && typeof payload.data === "object" ? payload.data : payload;
  const lists = [
    Array.isArray(payload) ? payload : null,
    Array.isArray(payload?.data) ? payload.data : null,
    Array.isArray(payload?.appointments) ? payload.appointments : null,
    Array.isArray(payload?.results) ? payload.results : null,
    Array.isArray(payload?.data?.appointments) ? payload.data.appointments : null,
    Array.isArray(payload?.data?.results) ? payload.data.results : null,
    Array.isArray(root?.results) ? root.results : null,
  ];
  const items = lists.find((list) => Array.isArray(list)) || [];
  const countRaw =
    payload?.count ??
    payload?.data?.count ??
    payload?.message?.count ??
    root?.count;
  const total = Number(countRaw);
  return {
    items: Array.isArray(items) ? items.filter(Boolean) : [],
    total: Number.isFinite(total) && total > 0 ? total : items.length,
  };
}

export function appointmentEvaluation(item) {
  const raw = pick(item, ["evaluation", "evaluation_id", "Evaluation"]);
  if (raw && typeof raw === "object") {
    return pick(raw, ["id", "pk"], "—");
  }
  return raw === "" ? "—" : raw;
}

export function appointmentDate(item) {
  return pick(nestedAppointment(item), ["date_selected", "date"], "—");
}

export function appointmentTime(item) {
  return pick(nestedAppointment(item), ["time_selected", "time", "slot"], "");
}

export function appointmentBookedAt(item) {
  return pick(nestedAppointment(item), ["date_applied", "created_at", "created"], "");
}

export function appointmentOfficeAddress(item) {
  return pick(nestedAppointment(item), ["Address", "address"], "—");
}

/** Appointment row used for status (list item or nested evaluation.appointment). */
export function appointmentStatusSource(item) {
  const nested = nestedAppointment(item);
  const id = pick(item, ["id", "pk", "appointment_id"]) ?? nested?.id;
  const status =
    item?.status ??
    item?.Status ??
    item?.appointment_status ??
    nested?.status ??
    nested?.Status ??
    nested?.appointment_status ??
    "";
  return {
    ...nested,
    id: id ?? nested?.id,
    status,
    date_selected: nested?.date_selected || item?.date_selected,
    date_applied: nested?.date_applied || item?.date_applied,
    time_selected: nested?.time_selected || item?.time_selected,
    Address: nested?.Address || item?.Address,
  };
}

export function appointmentStatusLabel(item) {
  return (
    formatAppointmentStatusLabel(appointmentStatusSource(item)) || "Pending"
  );
}

export function appointmentStatusTone(item) {
  return getAppointmentStatusTone(
    resolveAppointmentStatus(appointmentStatusSource(item)) || "pending"
  );
}

export function isAppointmentPending(item) {
  return (
    resolveAppointmentStatus(appointmentStatusSource(item)) === "pending"
  );
}

/**
 * PATCH api/v1/update_appointment_details/<pk>/ with { status }.
 * status: "approved" | "rejected"
 */
export async function updateAppointmentStatus({
  token,
  appointmentId: id,
  status,
}) {
  if (!token || !id || !status) {
    throw new Error("Missing appointment update details.");
  }
  const response = await fetch(
    API_ENDPOINT(`${Labels.update_appointment_details}${id}/`),
    {
      method: "PATCH",
      headers: authHeaders(token, true),
      body: JSON.stringify({ status }),
    }
  );
  const json = await readJson(response);
  if (!isApiSuccess(response, json)) {
    throw new Error(
      errorText(json, `Could not mark appointment as ${status}.`)
    );
  }
  return json;
}

export function applyAppointmentStatus(item, status) {
  if (!item || typeof item !== "object") return item;
  const nested = nestedAppointment(item);
  const nextNested =
    nested && typeof nested === "object"
      ? { ...nested, status }
      : nested;
  const next = { ...item, status };
  if (item.evaluation && typeof item.evaluation === "object") {
    const list = Array.isArray(item.evaluation.appointment)
      ? item.evaluation.appointment.map((row) =>
          String(row?.id) === String(item?.id) ||
          String(row?.id) === String(nextNested?.id)
            ? { ...row, status }
            : row
        )
      : item.evaluation.appointment;
    next.evaluation = { ...item.evaluation, appointment: list };
  }
  return next;
}

export function formatAppointmentTime(value) {
  const text = String(value || "").trim();
  if (!text) return "—";
  const parts = text.split(":");
  if (parts.length >= 2) {
    return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
  }
  return text;
}

export function formatAppointmentBookedAt(value) {
  const text = String(value || "").trim();
  if (!text) return "—";
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return text;
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, "0");
  const d = String(parsed.getDate()).padStart(2, "0");
  const hh = String(parsed.getHours()).padStart(2, "0");
  const mm = String(parsed.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${d} ${hh}:${mm}`;
}

export function matchesAppointmentSearch(item, query) {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const ev = evaluationOf(item);
  return [
    ev.Mileage,
    ev.reg_number,
    ev.brand_name,
    ev.brands,
    ev.model_name,
  ]
    .filter((value) => value != null && value !== "")
    .join(" ")
    .toLowerCase()
    .includes(q);
}

export function sortAppointments(list) {
  return [...list].sort((a, b) => {
    const dateCmp = String(appointmentDate(b)).localeCompare(
      String(appointmentDate(a))
    );
    if (dateCmp) return dateCmp;
    return String(appointmentTime(b)).localeCompare(String(appointmentTime(a)));
  });
}
